import { atom, useAtomValue } from 'jotai'
import { atomWithGlobalState } from '@highlight/ipc/jotai'
import {
  TRANSCRIPT_PRESENCE_KEY,
  EMPTY_TRANSCRIPT_PRESENCE,
  type TranscriptPresence,
} from '@shared/types/TranscriptPresence'
import { transcriptViewAtom } from '@renderer/overlay/Sidepanel/components/meeting/atoms'
import { transcriptMergingAtom } from './audio/use-audio-transcript-merging'
import { audioNoteStateAtom, selectTranscriptSource } from './audio/use-audio-transcript'

// Cheap presence booleans published by the main process (TranscriptManager).
// They only change on flips, unlike the entry arrays which broadcast every
// append — so subscribers here never wire up the heavy array atoms.
const transcriptPresenceAtom = atomWithGlobalState<TranscriptPresence>(
  TRANSCRIPT_PRESENCE_KEY,
  EMPTY_TRANSCRIPT_PRESENCE,
)

// Prefer the wire presence flag (entries are stripped from GlobalState). Fall
// back to legacy payloads that still embed transcriptEntries.
const hasSavedTranscriptAtom = atom((get) => {
  const state = get(audioNoteStateAtom)
  return !!state?.savedNoteHasTranscript || !!state?.savedNote?.transcriptEntries?.length
})
const savedTranscriptHasTextAtom = atom((get) => {
  const state = get(audioNoteStateAtom)
  if (state?.savedNoteHasTranscript) {
    return true
  }
  return !!state?.savedNote?.transcriptEntries?.some((e) => e.text.length > 0)
})

// Same source selection as activeTranscriptEntriesAtom, applied to the
// presence booleans.
const hasTranscriptAtom = atom((get) => {
  const presence = get(transcriptPresenceAtom)

  const source = selectTranscriptSource({
    view: get(transcriptViewAtom),
    isMerging: get(transcriptMergingAtom),
    hasProvider: presence.hasProvider,
    hasSaved: get(hasSavedTranscriptAtom),
    hasMerged: presence.hasMerged,
    hasProcessed: presence.hasProcessed,
  })

  switch (source) {
    case 'provider':
      return presence.providerHasText
    case 'saved':
      return get(savedTranscriptHasTextAtom)
    case 'merged':
      return presence.mergedHasText
    case 'processed':
      return presence.processedHasText
  }
})

const hasProviderTranscriptAtom = atom((get) => get(transcriptPresenceAtom).providerHasText)

export const useHasTranscript = () => {
  return useAtomValue(hasTranscriptAtom)
}

export const useHasProviderTranscript = () => {
  return useAtomValue(hasProviderTranscriptAtom)
}
